import React, { useState, useEffect, useMemo, useRef } from "react";
import { Dropdown } from "antd";
import { CheckOutlined, PlusOutlined } from "@ant-design/icons";
import SwitcherIcon from "../../../Assets/Icons/switcher.svg";
import ProfileIcon from "../../../Assets/Images/Profile.svg";
import "./sidebar.scss";
import { useNavigate } from "react-router-dom";
import Loader from "../Loader";
import { useDispatch, useSelector } from "react-redux";
import { setCurrentBusiness } from "../../../redux/slices/merchantSlice";
import { useLocation } from "react-router-dom";

const StatusBadge = ({ status }) => {
  let bgColor = "";
  let textColor = "";
  let text = "";

  switch (status) {
    case 'approved':
      bgColor = "bg-green-100";
      textColor = "text-green-700";
      text = "Approved";
      break;
    case 'pending':
      bgColor = "bg-yellow-100";
      textColor = "text-yellow-700";
      text = "Pending";
      break;
    default:
      return null;
  }

  return (
    <span
      className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${bgColor} ${textColor} ml-2`}
      style={{ letterSpacing: 0.1 }}
    >
      {text}
    </span>
  );
};

const BusinessSwitcher = () => {
  const location = useLocation();
  const currentPath = location?.pathname;
  const [businesses, setBusinesses] = useState([]);
  const [dropdownVisible, setDropdownVisible] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const navigate = useNavigate();
  const businessSwitcherRef = useRef(null);

  const dispatch = useDispatch();
  const selectedmerchant = useSelector(
    (state) => state.merchantReducer.selectedMerchent
  );
  const user = useSelector((state) => state.authReducer.tokenData?.user);

  const merchantProfilesFromRedux = useSelector((state) => state.merchantReducer.merchantProfiles);

  const sortedAndFilteredBusinesses = useMemo(() => {
    const profiles = merchantProfilesFromRedux?.data || user?.merchantProfiles || [];
    const filtered = profiles.filter(biz => biz.status !== 'declined');

    return filtered.sort((a, b) => {
      if (a.status === 'approved' && b.status !== 'approved') return -1;
      if (a.status !== 'approved' && b.status === 'approved') return 1;
      return a.businessName.localeCompare(b.businessName);
    });
  }, [merchantProfilesFromRedux?.data, user?.merchantProfiles]);

  useEffect(() => {
    console.log("Setting businesses and initial selection logic running...");
    setBusinesses(sortedAndFilteredBusinesses);

    if (sortedAndFilteredBusinesses.length > 0) {
      const storedSelectedBusinessId = localStorage.getItem('swoodle_selected_merchant_id');
      let businessToSet = null;

      if (storedSelectedBusinessId) {
        businessToSet = sortedAndFilteredBusinesses.find(p => p._id === storedSelectedBusinessId && p.status === 'approved');
      }

      if (!businessToSet) {
        businessToSet = sortedAndFilteredBusinesses.find(p => p.status === 'approved');
      }

      if (!businessToSet && sortedAndFilteredBusinesses.length > 0) {
        businessToSet = sortedAndFilteredBusinesses[0];
      }

      if (businessToSet && businessToSet._id !== selectedmerchant?._id) {
        console.log("Dispatching setCurrentBusiness:", businessToSet.businessName);
        dispatch(setCurrentBusiness(businessToSet));
      } else if (!businessToSet && selectedmerchant !== null) {
        console.log("Clearing selected business as no valid business found.");
        dispatch(setCurrentBusiness(null));
        localStorage.removeItem('swoodle_selected_merchant_id');
      }
    } else {
      if (selectedmerchant !== null) {
        console.log("No profiles available, clearing selected business.");
        dispatch(setCurrentBusiness(null));
      }
      setBusinesses([]);
      localStorage.removeItem('swoodle_selected_merchant_id');
    }
  }, [sortedAndFilteredBusinesses]);

  useEffect(() => {
    if (selectedmerchant) {
      localStorage.setItem('swoodle_selected_merchant_id', selectedmerchant._id);
    } else {
      localStorage.removeItem('swoodle_selected_merchant_id');
    }
  }, [selectedmerchant]);

  // ADDED: Sync selectedMerchant from latest Redux state on profile update
  useEffect(() => {
    const storedSelectedBusinessId = localStorage.getItem('swoodle_selected_merchant_id');

    if (storedSelectedBusinessId && merchantProfilesFromRedux?.data?.length > 0) {
      const updatedBusiness = merchantProfilesFromRedux.data.find(
        (biz) => biz._id === storedSelectedBusinessId
      );

      if (
        updatedBusiness &&
        (
          updatedBusiness._id !== selectedmerchant?._id ||
          updatedBusiness.businessName !== selectedmerchant?.businessName ||
          updatedBusiness.logo !== selectedmerchant?.logo
        )
      ) {
        dispatch(setCurrentBusiness(updatedBusiness));
      }
    }
  }, [merchantProfilesFromRedux?.data]);

  const handleBusinessSwitch = (biz) => {
    if (biz.status !== 'approved') {
      return;
    }

    if (selectedmerchant?._id !== biz._id) {
      setDropdownVisible(false);
      setLoading(true);

      dispatch(setCurrentBusiness(biz));
      setLoading(false);
    } else {
      setDropdownVisible(false);
    }
  };

  const menu = (
    <div
      className="business-switcher-dropdown"
      style={{
        background: "#fff",
        boxShadow: "0 4px 16px rgba(0,0,0,0.08)",
        borderRadius: 10,
        width: "100%",
        maxWidth: "100%",
        maxHeight: 220,
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <div style={{ overflowY: "auto", flexGrow: 1, paddingBottom: '12px' }}>
        {businesses.length > 0 ? (
          businesses.map((biz) => (
            <div
              key={biz._id}
              className={`dropdown-item flex items-center px-3 py-2 ${
                selectedmerchant && biz._id === selectedmerchant._id ? "selected" : ""
              } ${biz.status !== 'approved' ? "opacity-50 cursor-not-allowed" : "cursor-pointer hover:bg-[#f5faff]"}`}
              onClick={() => handleBusinessSwitch(biz)}
            >
              <img
                src={biz?.logo ? process.env.REACT_APP_S3_URL + biz?.logo : ProfileIcon}
                alt={biz?.businessName}
                className="business-logo w-9 h-9 rounded-full mr-3"
              />
              <div className="flex-1">
                <div className="business-name font-medium text-[15px] leading-tight">
                  {biz?.businessName}
                  <StatusBadge status={biz.status} />
                </div>
                <div className="business-email text-xs text-gray-400">
                  {user?.email}
                </div>
              </div>
              {selectedmerchant && biz._id === selectedmerchant._id && (
                <CheckOutlined className="text-[#00ADEF] ml-2" />
              )}
            </div>
          ))
        ) : (
          <div className="px-3 py-2 text-gray-500 text-sm">No businesses available.</div>
        )}
      </div>
      <div
        style={{
          width: "90%",
          margin: "0 auto",
          paddingTop: '12px',
          borderTop: '1px solid #eee',
          background: '#fff',
          position: 'sticky',
          bottom: 0,
          zIndex: 10,
        }}
      >
        <button
          className="create-business-btn flex items-center justify-center bg-[#00ADEF] text-white font-semibold text-[15px] h-[36px] rounded-[4px] transition-colors duration-150 hover:bg-[#0095c8] w-full mb-4"
          style={{
            boxShadow: "0 2px 8px rgba(0,173,239,0.10)",
            letterSpacing: 0.1,
            gap: 6,
          }}
          onClick={() => navigate("/merchant/getting-started")}
        >
          <span className="flex items-center justify-center w-3 h-3 rounded-full bg-white mr-1">
            <PlusOutlined className="text-[#00ADEF] text-[8px]" />
          </span>
          Create New Business
        </button>
      </div>
    </div>
  );

  return (
    <div className="business-switcher px-[20px] mb-3" ref={businessSwitcherRef}>
      {error && <div className="text-red-500 text-sm mb-2">{error}</div>}
      <Dropdown
        overlay={menu}
        trigger={["click"]}
        placement="bottomLeft"
        overlayClassName="pb-4 business-switcher-dropdown-wrapper"
        getPopupContainer={(trigger) => {
          const sidebarContainer = trigger?.closest('.sidebar-container');
          return sidebarContainer || document.body;
        }}
        disabled={loading || sortedAndFilteredBusinesses.length === 0 || currentPath.includes("/merchant/offer-form")}
        open={dropdownVisible}
        onOpenChange={(flag) => setDropdownVisible(flag)}
      >
        <div
          className={`current-business flex items-center py-2 px-2 rounded hover:bg-[#f5faff] ${
            currentPath.includes("/merchant/offer-form")
              ? "cursor-not-allowed opacity-50"
              : "cursor-pointer"
          }`}
        >
          {selectedmerchant ? (
            <>
              <img
                src={
                  (selectedmerchant?.logo
                    ? process.env.REACT_APP_S3_URL + selectedmerchant?.logo
                    : ProfileIcon)
                }
                alt={selectedmerchant?.businessName}
                className="business-logo w-9 h-9 rounded-full mr-3 truncate"
              />
              <div className="flex-1">
                <div className="flex business-name font-medium text-[15px] leading-tight whitespace-nowrap">
                  {(selectedmerchant?.businessName)?.length > 10
                    ? (selectedmerchant?.businessName).slice(0, 10) + "..."
                    : selectedmerchant?.businessName}
                </div>
                <div className="business-email text-xs text-gray-400">
                  {user?.email}
                </div>
              </div>
            </>
          ) : (
            <div className="flex-1 text-gray-400">No business found</div>
          )}
          <img
            src={SwitcherIcon}
            alt="switcher"
            className="ml-2 w-7 h-7 object-contain"
          />
        </div>
      </Dropdown>
      {loading && <Loader />}
    </div>
  );
};

export default BusinessSwitcher;
